import { useFetcher, useNavigate } from '@remix-run/react';
import {
	GQLCartFragment,
	GQLCategoryFragment,
	GQLOperationalSettingsFragment,
	GQLProductListingPageFragment,
	GQLProductTileFragment,
	GQLProductTileWithColourGroupsFragment,
} from '@theprintbar/graphql';
import { ImageSize, WebRoutes, cn, getSamedayName } from '@theprintbar/shared';
import { useEffect, useState } from 'react';
import {
	BackToTopButton,
	BannerHeader,
	Container,
	CustomColourIconHandler,
	ImageContainer,
	Modal,
	OutlineLinkButton,
	PrimaryButton,
	ProductTile,
	RichText,
	SearchBar,
	SimpleCTA,
} from '../../components';
import { Pagination } from '../../tables/PaginatedTable';
import { DesktopFilter } from './DesktopFilter';
import { MobileFilter } from './MobileFilter';
import { FilterListProps, SortBy } from './ProductFilter';
import { SamedayToggle } from './ProductFilter/SamedayToggle';
import { ProductQueryKeys, getProductSearchURL, hasProductFilters } from './ProductFilter/productSearch';

export interface ProductListingPageProps extends Omit<FilterListProps, 'expandAll'> {
	category?: GQLCategoryFragment;
	productListingPage: GQLProductListingPageFragment;
	operationalSettings: GQLOperationalSettingsFragment;
	initialProducts?: GQLProductTileFragment[] | GQLProductTileWithColourGroupsFragment[] | null;
	// Shown in place of the results when an unfiltered listing has no products assigned
	relatedProducts?: GQLProductTileFragment[] | null;
	customPillHandler?: (product: GQLProductTileFragment | GQLProductTileWithColourGroupsFragment) =>
		| {
				text: string;
				colour: string;
				icon: React.ReactNode;
		  }
		| undefined;
	customColourIconHandler?: CustomColourIconHandler;
	totalProducts?: number | null;
	webDomain?: string;
	productSearchURL?: string;
	crew?: boolean;
	sameDayText?: string;
	samedayCartWarning?: string | { document: any };
}

export function ProductListingPage({
	category,
	productListingPage,
	operationalSettings,
	initialProducts,
	relatedProducts,
	customPillHandler,
	customColourIconHandler,
	totalProducts,
	webDomain,
	crew,
	sameDayText,
	productSearchURL,
	samedayCartWarning,
	...filterProps
}: ProductListingPageProps) {
	const { categories, collections, store, params } = filterProps;
	const [products, setProducts] = useState(initialProducts || []);
	const productFetcher = useFetcher<ProductListingPageProps>();
	const cartFetcher = useFetcher<GQLCartFragment>();
	const [loadTriggered, setLoadTriggered] = useState<'empty' | 'initialised' | 'clicked'>('empty');
	const [parentCategory, childCategory] = params[ProductQueryKeys.Category] || [];
	const selectedCategory = categories?.find(c => c.code === (childCategory || parentCategory));
	const selectedDecoration = params[ProductQueryKeys.Decoration];
	const navigate = useNavigate();
	const [warningPopup, setWarningPopup] = useState(false);
	const decorationTitle = params?.decoration ? operationalSettings[`${params.decoration}Title`] : '';
	let decorationDescription: string | { document: { [key: string]: any } } = params.decoration
		? operationalSettings[`${params.decoration}LongDescription`]
		: '';

	if (typeof decorationDescription === 'string') {
		decorationDescription = {
			document: [
				{
					type: 'paragraph',
					children: [
						{
							text: decorationDescription,
						},
					],
				},
			],
		};
	} // Needed to convert decorationDescription to document type if it's a string

	useEffect(() => {
		if (cartFetcher.data && cartFetcher.data.sameday !== sameday) {
			setWarningPopup(true);
		}
	}, [cartFetcher.data]);

	useEffect(() => {
		setProducts(initialProducts || []);
		//Reset the fetcher
		productFetcher.data = undefined;
	}, [initialProducts]);

	useEffect(() => {
		if (!webDomain) {
			cartFetcher.load(WebRoutes.API.commerce.cart.get);
		}
	}, []);

	useEffect(() => {
		if (!productFetcher.data?.initialProducts || productFetcher.state === 'loading' || loadTriggered !== 'clicked') {
			return;
		}
		setProducts(productList => {
			const allProducts = [...productList, ...(productFetcher.data?.initialProducts || [])];
			const uniqueProducts = Array.from(new Map(allProducts.map(product => [product.id, product])).values());
			return uniqueProducts;
		});
		setLoadTriggered('empty');
	}, [productFetcher.data, loadTriggered]);

	const sameday = !!params[ProductQueryKeys.Sameday];

	const sortedCategories = categories?.sort((a, b) => a.order - b.order);
	const firstParent = sortedCategories?.find(c => !!category?.parents?.find(p => p.code === c.code));
	const topParent = sortedCategories?.find(c => !!firstParent?.parents?.find(p => p.code === c.code));

	const isWeb = !store && !crew;

	return !products ? null : (
		<>
			<Modal open={!!warningPopup} useLoading={false} className="pb-4" onClose={() => setWarningPopup(false)}>
				<div className="flex flex-col h-full">
					<div className="grid grid-cols-3 max-w-lg h-full">
						<img src="/images/error.png" className="w-full h-auto object-contain" />
						<div className="col-span-2 flex flex-col items-center space-y-4 ">
							<div>
								<RichText content={samedayCartWarning}></RichText>
							</div>
						</div>
					</div>
					<div className="flex justify-between items-center w-full mt-12">
						<OutlineLinkButton to={WebRoutes.Cart} onClick={() => setWarningPopup(false)}>
							View Cart
						</OutlineLinkButton>
						<PrimaryButton onClick={() => setWarningPopup(false)}>Close</PrimaryButton>
					</div>
				</div>
			</Modal>
			<div className="relative mb-20">
				<h1 className="hidden">
					{category?.headingTag || category?.metaTitle || productListingPage.headingTag || productListingPage.metaTitle}
				</h1>
				{isWeb && (
					<BannerHeader
						cta={{
							structure: {
								image: category?.metaImage || productListingPage.headerImage,
								title:
									params.action === 'duplicate'
										? productListingPage.duplicateTitle
										: `${sameday ? `${getSamedayName()} ` : ''}${
												category?.headingTag ||
												(selectedDecoration
													? decorationTitle || productListingPage.defaultTitle
													: selectedCategory?.name || productListingPage.defaultTitle)
										  }`,
								text: {
									content:
										params.action === 'duplicate'
											? productListingPage.duplicateDescription
											: category?.shortDescription || productListingPage.defaultDescription,
								},
							},
						}}
						breadcrumbs={[
							{
								name: 'Home',
								href: WebRoutes.Home,
								current: false,
							},
							...(topParent
								? [
										{
											name: topParent.name,
											href: WebRoutes.Collection(topParent.slug),
											current: false,
										},
								  ]
								: []),
							...(firstParent
								? [
										{
											name: firstParent.name,
											href: WebRoutes.Collection(firstParent.slug),
											current: false,
										},
								  ]
								: []),
							{
								name: category?.name || 'Products',
								href: category ? WebRoutes.Collection(category.slug) : WebRoutes.Products(),
								current: true,
							},
						]}
						bannerTextClassName="max-w-7xl mx-auto"
					/>
				)}
				<div className="flex flex-col lg:flex-row max-w-7xl mx-auto">
					{/* Filter */}

					<DesktopFilter
						{...filterProps}
						category={category}
						operationalSettings={operationalSettings}
						crew={crew}
						store={store}
					/>

					{/* with Product Section */}
					<div className={cn('flex-1', !!store || !!crew ? 'pl-0 md:pl-4 lg:pl-8' : 'p-4 lg:px-8')}>
						{/* Desktop Bar */}
						<div className="hidden lg:flex h-16 justify-between border-b-[1px] border-blue items-center w-full">
							<SamedayToggle {...filterProps} disabled={crew} />
							{/* Search Button */}
							<div className="flex justify-between lg:justify-end lg:space-x-6 items-center w-full lg:w-auto">
								<div className="hidden lg:block">
									<SearchBar
										currentSearch={params[ProductQueryKeys.Search] || ''}
										placeholder="Search for products"
										generateUrl={search => getProductSearchURL(ProductQueryKeys.Search, search, params)}
									/>
								</div>
								<SortBy params={params} products={products} setProducts={setProducts} />
							</div>
						</div>
						{/* Mobile Bar */}
						<div className="lg:hidden w-full">
							<div className="flex items-center justify-between gap-2 pr-2 pb-2 mb-2 border-b border-blue">
								<SamedayToggle
									{...filterProps}
									disabled={crew}
									className="relative top-0 lg:-top-3"
									fieldClassName="w-full justify-between"
								/>
							</div>
							<div className="flex items-center justify-between gap-2">
								<MobileFilter {...filterProps} category={category} operationalSettings={operationalSettings} />
								<SortBy
									className="lg:w-24 flex justify-center"
									params={params}
									products={products}
									setProducts={setProducts}
								/>
							</div>
						</div>

						<div className="hidden lg:flex items-center mt-4 space-x-2">
							<h6 className="pr-8">Collections</h6>
							<div className="flex flex-wrap gap-2">
								{collections?.map(({ id, icon, name, colour, code }) => (
									<button
										type="button"
										key={id}
										onClick={() => navigate(getProductSearchURL(ProductQueryKeys.Collection, code, params))}
										className={cn(
											'rounded-full px-3 py-1.5 flex items-center hover:opacity-80 uppercase text-blue border border-blue font-belfast font-bold text-xs',
											params.collection === code && 'ring-2 ring-blue',
										)}
										style={{ backgroundColor: colour || '' }}
									>
										{name}
										<ImageContainer size={ImageSize.Tiny} image={icon} className="w-4 h-4 ml-2" />
									</button>
								))}
							</div>
						</div>

						{/* Container for paddings */}
						<Container key={JSON.stringify(params)} className="px-0 lg:px-0">
							{/* Products */}
							{!products.length && relatedProducts?.length ? (
								<div className="mt-10">
									<h2 className="font-display font-light text-xl">
										New products coming soon to {category?.name || 'this range'}
									</h2>
									<p className="mt-2">In the meantime, here are some popular products.</p>
									<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mt-6">
										{relatedProducts.map(product => (
											<ProductTile
												store={store}
												webDomain={webDomain}
												productPath={store || crew ? product.slug : WebRoutes.Product(product.slug)}
												params={params}
												product={product}
												key={product.id}
												sameDayText={sameDayText}
												customPill={customPillHandler?.(product)}
												customColourIconHandler={customColourIconHandler}
											/>
										))}
									</div>
								</div>
							) : !products.length ? (
								<SimpleCTA
									content={{ simpleCTA: productListingPage.notFoundCTA }}
									containerClassName="pt-8 px-0 lg:px-0 my-0 lg:my-0"
								/>
							) : (
								<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mt-10">
									{products.map(product => {
										return (
											<ProductTile
												store={store}
												webDomain={webDomain}
												productPath={store || crew ? product.slug : WebRoutes.Product(product.slug)}
												params={params}
												product={product}
												key={product.id}
												colourGroup={params[ProductQueryKeys.Color]}
												sameDayText={sameDayText}
												customPill={customPillHandler?.(product)}
												customColourIconHandler={customColourIconHandler}
											/>
										);
									})}
								</div>
							)}

							<Pagination
								searchParams={params}
								total={totalProducts}
								itemsPerPageOptions={[24, 48, 96]}
								linkPages={!hasProductFilters(params)}
								linkItemsPerPage={false}
							/>
							{!!category?.SEODescription && (
								<div className="mt-10">
									<RichText content={category.SEODescription} />
								</div>
							)}
						</Container>
					</div>
				</div>

				<BackToTopButton className="hidden lg:flex w-max my-auto lg:ml-8">Back to top</BackToTopButton>
			</div>
		</>
	);
}
